import React, { useEffect, useState } from 'react'
import { Modal } from '@pandiafi/components'
import { Button, Paragraph } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { useBlocker } from 'react-router-dom'

interface TerminationNavigationGuardProps {
  shouldWarn: boolean
}

export const TerminationNavigationGuard: React.FC<
  TerminationNavigationGuardProps
> = ({ shouldWarn }) => {
  const { t } = useTranslation(['terminateContract'])
  const blocker = useBlocker(shouldWarn)
  const [showConfirmation, setShowConfirmation] = useState(false)

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowConfirmation(true)
    }
  }, [blocker.state])

  useEffect(() => {
    if (!shouldWarn) {
      return
    }

    const preventUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = true
    }

    window.addEventListener('beforeunload', preventUnload)

    return () => window.removeEventListener('beforeunload', preventUnload)
  }, [shouldWarn])

  const continueFillingForm = () => {
    setShowConfirmation(false)
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }

  const leaveForm = () => {
    setShowConfirmation(false)
    if (blocker.state === 'blocked') {
      blocker.proceed()
    }
  }

  return (
    <Modal
      show={showConfirmation}
      onClose={continueFillingForm}
      title={t('terminateContract:headings.leavePreviewConfirmation')}
      width="s"
    >
      <Modal.Content>
        <Paragraph>
          {t('terminateContract:texts.leavePreviewConfirmation')}
        </Paragraph>
      </Modal.Content>
      <Modal.Buttons>
        <Button variant="cancel" onClick={leaveForm}>
          {t('terminateContract:buttons.leavePreview')}
        </Button>
        <Button variant="primary" onClick={continueFillingForm}>
          {t('terminateContract:buttons.continueFilling')}
        </Button>
      </Modal.Buttons>
    </Modal>
  )
}
